import React, { useCallback, useRef, useState } from 'react';
import { ATMButton, ATMModal } from 'shared-it-appmod-ui';
import {
  ToastSuccess,
  ToastError,
} from 'src/components/atoms/toaster/toaster.component';
import { ISensitiveCommunities, ISensitiveCommunitiesForm } from 'src/models/sensitive-communities.model';
import { useSensitiveCommunitiesContext } from 'src/contexts/sensitive-communities.context';
import Lang from 'src/libraries/language';
import { actionTypes } from 'src/ducks/sensitive-communities.duck';
import { getSensitiveCommunitiesStatus } from 'src/selectors/sensitive-communities.selector';
import SensitiveCommunitiesForm from '../sensitive-communities-form/sensitive-communities-form.component';
import style from '../sensitive-communities-list/sensitive-communities-list.module.scss';

type IProp = {
  data: ISensitiveCommunities;
};

const SensitiveCommunitiesEdit: React.FC<IProp> = ({ data }) => {
  const { state, actions } = useSensitiveCommunitiesContext();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  let defaultValues: Partial<ISensitiveCommunities> = {
    sensitiveCommunity: '',
    sensitiveCommunityPamLead: '',
  };

  if (data) {
    defaultValues = data;
  }

  const editStatus = getSensitiveCommunitiesStatus(state, actionTypes.SENSITIVE_COMMUNITIES_UPDATE);

  const handleSave = useCallback(() => {
    if (formRef && formRef.current) {
      formRef.current.handleSubmit();
    }
  }, [formRef]);

  const handleSubmit = useCallback(
    async (value) => {
      const formData: ISensitiveCommunitiesForm = {
        sensitiveCommunity: value.sensitiveCommunity,
        sensitiveCommunityPamLead: value.sensitiveCommunityPamLead
      };
      const response = await actions.updatePUT(value.sensitiveCommunityId, formData);
      if (!response.error) {
        ToastSuccess(
          Lang.formatString(
            Lang.MSG_SENSITIVE_COMMUNITY_EDIT_SUCCESS,
            formData.sensitiveCommunity ? formData.sensitiveCommunity : value.sensitiveCommunity
          )
        );
      } else {
        ToastError(response.error.message);
      }
      setIsEditOpen(false);
      actions.listGET();
    },
    [actions, setIsEditOpen]
  );

  return (
    <ATMModal
      open={isEditOpen}
      size="tiny"
      trigger={
        <ATMButton
          className={style.actionIcon}
          basic
          icon="edit outline"
          onClick={() => setIsEditOpen(true)}
        />
      }
      header={Lang.LBL_ADD_SENSITIVE_COMMUNITIES}
      content={
        <div className={style.content}>
          <SensitiveCommunitiesForm
            defaultValues={defaultValues}
            formRef={formRef}
            handleSubmit={handleSubmit}
            handleEnable={(value) => setIsDirty(value)}
          />
        </div>
      }
      actions={
        <div className={style.actionBtns}>
          <ATMButton
            basic
            color="blue"
            content={Lang.LBL_CANCEL}
            onClick={() => {
              setIsEditOpen(false);
            }}
          />
          <ATMButton
            primary
            content={Lang.LBL_SAVE}
            disabled={!isDirty || editStatus.fetching}
            onClick={handleSave}
            loading={editStatus.fetching}
          />
        </div>
      }
    />
  );
};

export default SensitiveCommunitiesEdit;
